import type { ReactNode } from "react";

/**
 * "Your session has ended", from `02 Console Shell v3.dc.html`, state `signedout`.
 *
 * The design draws this inside the shell's `<main>`, in place of the page, with the rail and the top
 * bar still there and the tenant still named. That placement is the design's answer to a question
 * worth being explicit about: an operator whose session has ended is not shown a different
 * application, they are shown the same console telling them one thing is missing.
 *
 * It is reached only when a session genuinely cannot be renewed. `ConsoleAuthority.accessToken()`
 * refreshes a token before a request is allowed to carry it, and `useAuthentication` renews an idle
 * one ahead of its expiry, so arriving here means the refresh grant itself was refused — the
 * provider's session is over and no amount of retrying will produce a token. Which is why the one
 * control is "Sign in again" and there is no retry.
 *
 * "Sign in to come back here" is kept because it is true: the page is in the URL, the sign-in flow
 * stores that URL as its return address, and the operator lands back on the board they were working.
 */

export interface SessionEndedProps {
  readonly tenantName: string;
  /** Epoch milliseconds the session was found to be over. */
  readonly endedAt: number;
  /** How long it had lasted, so the card can say what the design's card says. */
  readonly lastedMs: number;
  readonly onSignInAgain: () => void;
}

/**
 * "12 hours", in the design's own register.
 *
 * Whole units, because the sentence is orientation rather than measurement: an operator reading it
 * wants to know whether their shift ran out or something went wrong ten minutes in, and "11 hours 48
 * minutes" answers neither question better than "11 hours".
 */
export function lastedFor(lastedMs: number): string {
  const minutes = Math.round(lastedMs / 60_000);
  if (minutes < 1) return "less than a minute";
  if (minutes < 60) return minutes === 1 ? "1 minute" : `${String(minutes)} minutes`;
  const hours = Math.round(minutes / 60);
  return hours === 1 ? "1 hour" : `${String(hours)} hours`;
}

/** The clock the design writes, on the operator's own timezone rather than the design file's. */
export function endedAtClock(endedAt: number): string {
  return new Date(endedAt).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZoneName: "short",
  });
}

export function SessionEnded({
  tenantName,
  endedAt,
  lastedMs,
  onSignInAgain,
}: SessionEndedProps): ReactNode {
  return (
    <div className="ended">
      <div className="ended__card">
        <span aria-hidden="true" className="ended__glyph">
          key
        </span>
        <div className="ended__text">
          <h1 className="ended__headline">Your session has ended</h1>
          <p className="ended__detail">
            You were signed out at {endedAtClock(endedAt)} after {lastedFor(lastedMs)}. Nothing you
            submitted is lost. Sign in to come back here with {tenantName} selected.
          </p>
        </div>
        <button type="button" className="ended__action" onClick={onSignInAgain}>
          Sign in again
        </button>
      </div>
    </div>
  );
}
